(() => {
  const cardSelector = '[data-goggle-variant-card][data-goggle-filter-variant-id]';
  const lensColorParam = 'filter.v.m.custom.goggles_lens_color_swatch';
  const listingGroupParam = 'filter.v.m.custom.goggle_listing_groups';
  let availableLensColorIds = null;
  let availableListingGroupIds = null;

  const elementsWithin = (root, selector) => {
    const elements = root instanceof Element && root.matches(selector) ? [root] : [];
    return elements.concat([...root.querySelectorAll(selector)]);
  };

  const readVariantMap = (card) => {
    const source = card.querySelector('[data-goggle-variant-map]');
    if (!source) return [];
    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      return [];
    }
  };

  const updateVariantLinks = (card, variantId) => {
    card.querySelectorAll('[data-grid-link]').forEach((link) => {
      const destination = new URL(link.getAttribute('href'), window.location.origin);
      destination.searchParams.set('variant', variantId);
      link.setAttribute('href', `${destination.pathname}${destination.search}${destination.hash}`);
    });
  };

  const suppressMobileFiltersDrawer = () => {
    const mobileBreakpoint = window.theme?.sizes?.small || 768;
    if (window.innerWidth >= mobileBreakpoint) return;

    document.addEventListener('theme:scroll:lock', (event) => {
      const filtersList = event.detail;
      if (!filtersList?.matches?.('[data-collection-filters-list]')) return;

      const unlock = () => {
        document.querySelectorAll('.collection__filters.collection__filters--visible').forEach((filters) => {
          filters.classList.remove('collection__filters--visible');
        });
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        document.documentElement.removeAttribute('data-scroll-locked');
      };

      window.setTimeout(unlock, 50);
      window.setTimeout(unlock, 250);
    }, {once: true});
  };

  const applyQuickFilter = (button) => {
    const form = document.querySelector('[data-collection-filters-form]');
    const paramName = button.dataset.filterParam;
    if (!form || !paramName) return;

    const inputs = [...form.querySelectorAll('input[type="checkbox"], input[type="radio"]')]
      .filter((input) => input.name === paramName);
    const selectedValue = button.dataset.filterValue || '';
    const deactivate = button.getAttribute('aria-pressed') === 'true';

    inputs.forEach((input) => {
      input.checked = !deactivate && selectedValue !== '' && input.value === selectedValue;
    });

    button.closest('[data-goggle-quick-filter]')?.classList.add('is-loading');
    suppressMobileFiltersDrawer();
    form.dispatchEvent(new Event('input', {bubbles: true}));
  };

  const lensColorIdFromValue = (value) => String(value || '').split('/').pop();

  const collectAvailableVariantFilters = () => {
    const lensColorIds = new Set();
    const listingGroupIds = new Set();

    document.querySelectorAll('[data-goggle-variant-map]').forEach((source) => {
      try {
        JSON.parse(source.textContent)
          .filter((variant) => variant.available)
          .forEach((variant) => {
            if (variant.lensColorId) lensColorIds.add(String(variant.lensColorId));
            (variant.goggleListingGroupIds || []).forEach((id) => listingGroupIds.add(String(id)));
          });
      } catch (error) {
        return;
      }
    });

    return {lensColorIds, listingGroupIds};
  };

  const updateQuickFilterState = () => {
    const activeLensColor = new URLSearchParams(window.location.search).has(lensColorParam);
    const activeListingGroup = new URLSearchParams(window.location.search).has(listingGroupParam);
    if (!activeLensColor && !activeListingGroup) {
      const availableFilters = collectAvailableVariantFilters();
      if (availableFilters.lensColorIds.size > 0) availableLensColorIds = availableFilters.lensColorIds;
      if (availableFilters.listingGroupIds.size > 0) availableListingGroupIds = availableFilters.listingGroupIds;
    }

    document.querySelectorAll('[data-goggle-quick-filter]').forEach((quickFilter) => {
      quickFilter.querySelectorAll('[data-goggle-quick-filter-button]').forEach((button) => {
        const value = button.dataset.filterValue;
        const availableIds = button.dataset.filterParam === lensColorParam
          ? availableLensColorIds
          : availableListingGroupIds;
        button.hidden = Boolean(availableIds && value !== '' && !availableIds.has(lensColorIdFromValue(value)));
      });
    });

    const products = document.querySelector('[data-collection-products]');
    const noResults = products?.querySelector('[data-goggle-lens-no-results]');
    if (noResults) {
      noResults.hidden = (!activeLensColor && !activeListingGroup) || products.querySelectorAll('[data-product-item]').length > 0;
    }
  };

  const selectFilteredVariant = (card, attempt = 0) => {
    if (card.dataset.goggleVariantApplied === card.dataset.goggleFilterVariantId) return;
    const variantId = String(card.dataset.goggleFilterVariantId || '');
    const variant = readVariantMap(card).find((item) => String(item.variantId) === variantId && item.available);
    if (!variant) return;

    updateVariantLinks(card, variantId);

    const swatches = card.querySelector('product-grid-item-swatch');
    if (!swatches || typeof swatches.updateImagesAndLinksOnEvent !== 'function') {
      if (attempt < 20) window.setTimeout(() => selectFilteredVariant(card, attempt + 1), 100);
      return;
    }

    swatches.updateImagesAndLinksOnEvent(variantId);
    card.dataset.goggleVariantApplied = variantId;
  };

  const initializeCards = (root = document) => {
    elementsWithin(root, cardSelector).forEach((card) => selectFilteredVariant(card));
  };

  const itemFromLink = (link) => ({
    item_id: link.dataset.productId,
    product_id: link.dataset.productId,
    variant_id: link.dataset.variantId,
    item_name: link.dataset.itemName,
    item_variant: link.dataset.itemVariant,
    item_brand: link.dataset.brand,
    brand: link.dataset.brand,
    index: Number(link.dataset.position),
    position: Number(link.dataset.position)
  });

  const trackListView = (list) => {
    if (list.dataset.analyticsViewed === 'true') return;
    const links = [...list.querySelectorAll('[data-goggle-list-link]')];
    if (!links.length) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: list.dataset.listId,
        item_list_name: list.dataset.listName,
        items: links.map(itemFromLink)
      }
    });
    list.dataset.analyticsViewed = 'true';
  };

  const initializeAnalytics = (root = document) => {
    elementsWithin(root, '[data-goggle-variant-list]').forEach((list) => {
      if (list.dataset.analyticsInitialized === 'true') return;
      list.dataset.analyticsInitialized = 'true';
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            trackListView(list);
            observer.disconnect();
          }
        }, {threshold: 0.2});
        observer.observe(list);
      } else {
        trackListView(list);
      }
    });
  };

  document.addEventListener('click', (event) => {
    const quickFilterButton = event.target.closest('[data-goggle-quick-filter-button]');
    if (quickFilterButton) {
      event.preventDefault();
      applyQuickFilter(quickFilterButton);
      return;
    }

    const link = event.target.closest('[data-goggle-list-link]');
    if (!link) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'select_item',
      list_id: link.dataset.listId,
      list_name: link.dataset.listName,
      product_id: link.dataset.productId,
      variant_id: link.dataset.variantId,
      item_name: link.dataset.itemName,
      item_variant: link.dataset.itemVariant,
      brand: link.dataset.brand,
      position: Number(link.dataset.position),
      ecommerce: {
        item_list_id: link.dataset.listId,
        item_list_name: link.dataset.listName,
        items: [itemFromLink(link)]
      }
    });
  });

  const initialize = (root = document) => {
    initializeCards(root);
    initializeAnalytics(root);
    updateQuickFilterState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
      });
    });
  }).observe(document.documentElement, {childList: true, subtree: true});
})();
